import React, { createContext, useContext, useState, useEffect } from 'react';
import { login as apiLogin, ApiResponse } from '../../utils/api';

interface User {
  id: number;
  name: string;
  email: string;
  club: string;
  email_verified: boolean;
  is_admin?: boolean;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<ApiResponse>;
  logout: () => void;
  isLoading: boolean;
  updateUserData: (data: User) => void;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  isAuthenticated: false,
  login: async () => ({ success: false, message: 'Not implemented' }),
  logout: () => {},
  isLoading: false,
  updateUserData: () => {},
  isAdmin: false,
});

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(() => {
    const savedUser = localStorage.getItem('user');
    return savedUser ? JSON.parse(savedUser) : null;
  });
  const [isLoading, setIsLoading] = useState(false);

  // Admin-Status aus den vom Server gelieferten Benutzerdaten lesen
  const isAdmin = user?.is_admin === true;
  const isAuthenticated = !!user;

  const updateUserData = (data: User) => {
    setUser(data);
    localStorage.setItem('user', JSON.stringify(data));
  };

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      const response = await apiLogin({ email, password });
      if (response.success && response.data) {
        updateUserData(response.data);
        localStorage.setItem('lastActivity', Date.now().toString());
      }
      return response;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    fetch('/api/auth/logout.php', { credentials: 'include' }).catch(() => {});
    setUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('lastActivity');
  };

  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      setUser(JSON.parse(savedUser));
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, login, logout, isLoading, updateUserData, isAdmin }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
